/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Action } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { createStateMutation } from './createStateMutation';
import {
  ExtendableListPaginationQueryArgBase,
  ExtendableListPaginationQueryResponseBase,
  ExtendableListPaginationState,
  ExtractResponseAdditionalData,
} from './types';

type EndpointQueryLike<QueryArg, Result> = {
  select: (arg: QueryArg) => (state: any) => {
    data?: Result;
    isLoading: boolean;
    isUninitialized: boolean;
  };
  initiate: (arg: QueryArg) => any;
};

const DEFAULT_PAGE = 1;

type UseExtendableListPaginationReturn<Item, ResultType extends ExtendableListPaginationQueryResponseBase<Item>> = {
  /**
   * All loaded items
   */
  items: Item[];

  /**
   * Data returned from query without items parameter
   */
  additionalData?: ExtractResponseAdditionalData<Item, ResultType>;

  isLoading: boolean;
  canLoadMore: boolean;
  onLoadMore: () => void;
};

/**
 * Implementation of extendable list pagination.
 * Should be used when we have list with "load more" button,
 * which loads new items and adds them to the end of the list.
 */
export const useExtendableListPagination = <
  QueryArg extends ExtendableListPaginationQueryArgBase,
  Parameters extends Omit<QueryArg, 'page'>,
  ResultType extends ExtendableListPaginationQueryResponseBase<Item>,
  Item = ResultType['items'][number],
>({
  parameters,
  query,
}: {
  /**
   * Query parameters without `page` parameter.
   * Page parameter is calculated inside this hook.
   */
  parameters: Parameters;
  query: EndpointQueryLike<QueryArg, ResultType>;
}): UseExtendableListPaginationReturn<Item, ResultType> => {
  const dispatch = useDispatch() as ThunkDispatch<any, void, Action>;
  const { data, isLoading, isUninitialized } = useSelector(query.select({ ...parameters, page: DEFAULT_PAGE } as any));
  const { items = [], ..._additionalData } = data ?? {};
  const additionalData = _additionalData as ExtractResponseAdditionalData<Item, ResultType>;

  const [state, setState] = React.useState<ExtendableListPaginationState<Parameters, Item, ResultType>>({
    items,
    totalCount: additionalData.totalCount ?? 0,
    parameters,
    page: DEFAULT_PAGE,
    additionalData,
  });
  const canLoadMore = state.items.length < state.totalCount;

  const requestData = React.useCallback(
    async ({ page }: { page: number }) => {
      const { data: response } = await dispatch(query.initiate({ ...parameters, page } as any));

      if (response) {
        setState(createStateMutation({ parameters, response: response as ResultType, page }));
      }
    },
    [query, parameters],
  );

  const onLoadMore = React.useCallback(() => {
    if (!canLoadMore) {
      return;
    }

    requestData({ page: state.page + 1 });
  }, [canLoadMore, requestData]);

  React.useEffect(() => {
    if ((isUninitialized && !isLoading) || !isEqual(state.parameters, parameters)) {
      requestData({ page: DEFAULT_PAGE });
    }
  }, [isUninitialized, isLoading, state.parameters, parameters, requestData]);

  return {
    items: state.items,
    isLoading,
    canLoadMore,
    onLoadMore,
    additionalData,
  };
};
